import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios'; // ✅ Import axios for API calls
import { UserContext } from '../../UserContext'; // ✅ Import User Context

const Hydration = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = 'hydration'; // ✅ Category for fetching videos
  const [videos, setVideos] = useState([]); // ✅ State to hold videos
  const [loading, setLoading] = useState(true); // ✅ Loading state

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful for logged-in users only
  const handleMarkHelpful = async (videoId) => {
    if (!user || !user.id) {
      alert('You must be logged in to mark as helpful.');
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error('Error marking video as helpful:', error);
    }
  };

  return (
    <div className="hydration-page page-container">
      <h1 className="page-title">Hydration</h1>

      <img
        src="/images/wf-hydration.webp"
        alt="Hydration Illustration"
        className="page-image"
        width="330"
      />

      <p className="page-description">
        Welcome to the Hydration section of Wholeness Forward. Proper hydration is essential for maintaining
        overall health and well-being. Staying adequately hydrated can improve physical performance, boost energy levels,
        support digestion, enhance skin health, and improve cognitive function.
      </p>

      <h2 className="section-title">How Hydration Impacts Your Health</h2>
      <ul className="content-list">
        <li><strong>Boosts Physical Performance:</strong> Maintains energy and endurance during exercise.</li>
        <li><strong>Enhances Digestion:</strong> Helps break down food and absorb nutrients efficiently.</li>
        <li><strong>Supports Skin Health:</strong> Prevents dryness and premature aging.</li>
        <li><strong>Improves Mental Focus:</strong> Boosts cognitive function, improving concentration.</li>
        <li><strong>Helps Detoxify the Body:</strong> Flushes out toxins, reducing the burden on kidneys and liver.</li>
      </ul>

      <h3 className="section-title">How Much Water Should You Drink?</h3>
      <p className="section-description">
        The general guideline is at least 8 cups (64 ounces) per day, but needs vary based on size, activity level, and environment.
      </p>

      <h3 className="section-title">Signs of Dehydration</h3>
      <ul className="content-list">
        <li>Thirst</li>
        <li>Dry mouth or skin</li>
        <li>Fatigue or dizziness</li>
        <li>Dark yellow urine or infrequent urination</li>
        <li>Headaches or muscle cramps</li>
      </ul>

      <hr className="section-divider" />

      <h2 className="section-title">Curated Video Collection</h2>

      {loading ? (
        <p className="loading-message">Loading videos...</p>
      ) : (
        <div className="video-container">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div key={video._id} className="video-card">
                <h3 className="video-title">{video.title}</h3>
                <p className="video-details">
                  <strong>Source:</strong> {video.source} <br />
                  <strong>Length:</strong> {video.length} <br />
                  {video.description}
                </p>
                <iframe
                  className="video-frame"
                  width="560"
                  height="315"
                  src={video.url}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>

                {/* ✅ Helpful Button */}
                <button
                  className="button"
                  onClick={() => handleMarkHelpful(video._id)}
                  disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                >
                  {video.usersWhoClickedHelpful?.includes(user?.id)
                    ? `Helpful (${video.helpfulCount})`
                    : `Mark as Helpful (${video.helpfulCount || 0})`}
                </button>
              </div>
            ))
          ) : (
            <p className="no-videos-message">No videos found for this category.</p>
          )}
        </div>
      )}

      <hr className="section-divider" />

      <h3 className="section-title">Stay Hydrated</h3>
      <p className="section-description">
        Discover practical tips, explore hydration myths, and learn how to personalize your hydration strategy.
      </p>

      <a href="/forum/Hydration" className="btn">Join the Hydration Forum</a>
    </div>
  );
};

export default Hydration;
